import {
  AfterViewChecked,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ObjectTypeInfoColumnType,
  ObjectTypeInfoColumnViewModel,
  ObjectTypeInfoReferenceViewModel,
  ObjectTypeInfoReferencingViewModel,
  ObjectTypeInfoViewModel,
} from '@barthauer/abc/core/data';
import { ColumnTypeIcon, FilterOption } from '@barthauer/abc/queries/opath/core';
import { _AbcTreeComponent, _AbcTreeNode } from '@barthauer/abc/ui-components';
import { DxScrollViewComponent } from 'devextreme-angular';
import { ItemClickEvent, ItemExpandedEvent } from 'devextreme/ui/tree_view';
import { HeaderButton } from 'libs/abc/queries/opath/core/src/lib/viewModels/layout.viewModel';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbcExpressionsEditorService } from '../../expressions-editor.service';
import { EntityButtonId, SelectedEntity } from '../../models/entity.model';
import { NodeTypeFilter, TranslationConfiguration } from '../../models/view.model';
import { SharedService } from '../../services/shared.service';

@Component({
  selector: 'expressions-editor-entity',
  templateUrl: './entity.component.html',
  styleUrls: ['./entity.component.scss'],
})
export class EntityComponent implements OnDestroy, AfterViewChecked, OnInit, OnChanges {
  @ViewChild('entityTree') entityTree: _AbcTreeComponent;
  @ViewChild('scroller') scroller: DxScrollViewComponent;
  @Output() onEntitySelected: EventEmitter<SelectedEntity> = new EventEmitter<SelectedEntity>();
  @Output() onColumnsDictionaryUpdated: EventEmitter<void> = new EventEmitter<void>();
  @Output() onPinChange: EventEmitter<void> = new EventEmitter<void>();
  @Input() neutralDesignationActive: boolean;
  @Input() set entityData(data: ObjectTypeInfoViewModel) {
    this.objectTypeInfo = data;
    this.rootTreeNodes = this.createTreeData(this.objectTypeInfo);
  }

  private _pinned: boolean = true;
  @Input()
  set pinned(value: boolean) {
    this._pinned = value;
    this.refreshButtonsState();
  }
  get pinned(): boolean {
    return this._pinned;
  }
  public _showPrefernceColumn: boolean = true;

  set showPrefernceColumn(value: boolean) {
    this._showPrefernceColumn = value;
  }
  get showPrefernceColumn(): boolean {
    return this._showPrefernceColumn;
  }
  public _showKeyColumn: boolean = true;

  set showKeyColumn(value: boolean) {
    this._showKeyColumn = value;
  }
  get showKeyColumn(): boolean {
    return this._showKeyColumn;
  }

  public entityButtons: HeaderButton[] = [];

  public currentFilterOption: FilterOption | undefined;
  public rootTreeNodes: _AbcTreeNode[];
  public translation: TranslationConfiguration;
  public searchTerm: string = '';
  public isPrefernce: boolean = true;

  public searchMode: string;
  public columnsDictionary: { key: string; value: string }[] = [];

  private objectTypeInfo: ObjectTypeInfoViewModel;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private EMPTYSTRING: string = '';

  constructor(
    private sharedService: SharedService,
    protected abcExpressionsEditorService: AbcExpressionsEditorService
  ) {
    this.translation = this.sharedService.translation;
    this.refreshButtonsState();
  }

  async ngOnInit(): Promise<void> {
    this.abcExpressionsEditorService.nodesTypeFilter.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.filterTreeByActiveKeys(this.rootTreeNodes[0].children);
      this.entityTree?.instance?.repaint();
    });
    this.toggleNeutralDesignation();
  }

  ngAfterViewChecked(): void {
    this.entityTree?.searchValueChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.entityTree.instance.collapseAll();
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['neutralDesignationActive']) {
      this.toggleNeutralDesignation();
    }
  }

  ngAfterViewInit(): void {
    this.entityTree.searchValueChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      setTimeout(() => {
        this.collapseEmptyNodes(this.rootTreeNodes[0]);
      }, 0);
    });
  }

  public pin(): void {
    this.onPinChange.emit();
  }

  /**
   * Collapses a given node if it is empty and all its children if they are too.
   * @param node
   * @returns void
   */
  private collapseEmptyNodes(node: _AbcTreeNode): void {
    for (let leaf of node.children) {
      if (leaf.children.length && !leaf.children[0].visible) {
        this.entityTree.instance.collapseItem(leaf);
      } else {
        this.collapseEmptyNodes(leaf);
      }
    }
  }

  public toggleNeutralDesignation = (): void => {
    this.translateNode(this.rootTreeNodes[0]);
  };

  public getIcon(iconName: string): string {
    return this.sharedService.getIcon(iconName);
  }

  public onItemClick(event: ItemClickEvent): void {
    const treeNode: _AbcTreeNode = event.itemData as _AbcTreeNode;

    const isReferencing = treeNode.data instanceof ObjectTypeInfoReferencingViewModel;

    let parentNode:
      | ObjectTypeInfoColumnViewModel
      | ObjectTypeInfoReferenceViewModel
      | ObjectTypeInfoReferencingViewModel = undefined;

    if (event.event.ctrlKey) {
      parentNode = treeNode.parentNode.data as
        | ObjectTypeInfoColumnViewModel
        | ObjectTypeInfoReferenceViewModel
        | ObjectTypeInfoReferencingViewModel;
    }

    this.onEntitySelected.emit({
      id: treeNode.id,
      mainObjectTypeInfoViewModel: this.objectTypeInfo,
      objectInfo: treeNode.data as
        | ObjectTypeInfoColumnViewModel
        | ObjectTypeInfoReferenceViewModel
        | ObjectTypeInfoReferencingViewModel,
      isCTRLKeyHold: event.event.ctrlKey,
      parentObjectInfo: parentNode,
      isReferencing: isReferencing,
    });
  }

  public async onItemExpanded(event: ItemExpandedEvent): Promise<void> {
    let scrollToNode: boolean = false;
    const treeNode: _AbcTreeNode = event.itemData as _AbcTreeNode;
    scrollToNode = treeNode.children.filter((child) => child.visible === false).length === 1;

    await this.expandTreeItem(treeNode);
    this.filterTreeByActiveKeys(treeNode.children);
    this.recoverScrollPosition(scrollToNode);
  }

  private recoverScrollPosition(scrollToNode: boolean): void {
    let scrollPosition: number = 0;
    scrollPosition = this.scroller.instance.scrollOffset();

    // ACH: not happy with this
    setTimeout(() => {
      scrollToNode ? this.scroller.instance.scrollBy(scrollPosition) : null;
    }, 1);
  }

  public searchTermChanged(searchTerm: string): void {
    this.searchTerm = searchTerm;
  }

  public filterOptionChanged(filterOption: FilterOption): void {
    this.currentFilterOption = filterOption;
  }

  private async expandTreeItem(treeNode: _AbcTreeNode): Promise<void> {
    if (
      (treeNode.data instanceof ObjectTypeInfoReferenceViewModel ||
        treeNode.data instanceof ObjectTypeInfoReferencingViewModel) &&
      treeNode.children.length === 1
    ) {
      const objectTypeInfo: ObjectTypeInfoViewModel = treeNode.data.objectType;

      treeNode.children = [];
      this.createTreeColumns(objectTypeInfo.columnsByName, treeNode);

      this.createTreeExpandableColumns(objectTypeInfo.referencesByName, treeNode);
      this.createTreeExpandableColumns(objectTypeInfo.referencingByName, treeNode);

      // TODO Kann man evtl. optimieren
      for (const child of treeNode.children) {
        this.entityTree.addNode(child);
      }
    }
  }

  public async toggleNodesByType(nodeType: string): Promise<void> {
    nodeType === NodeTypeFilter.Key
      ? (this.showKeyColumn = !this.showKeyColumn)
      : (this.showPrefernceColumn = !this.showPrefernceColumn);
    await this.displayNodesByType(nodeType);
  }

  private async displayNodesByType(nodeType: string): Promise<void> {
    const nodesTypeFilterValue = this.abcExpressionsEditorService.nodesTypeFilter.value;

    if (nodesTypeFilterValue.includes(nodeType)) {
      const index = nodesTypeFilterValue.indexOf(nodeType);
      const updatedFilter = [...nodesTypeFilterValue.slice(0, index), ...nodesTypeFilterValue.slice(index + 1)];
      this.abcExpressionsEditorService.nodesTypeFilter.next(updatedFilter);
    } else {
      this.abcExpressionsEditorService.nodesTypeFilter.next([...nodesTypeFilterValue, nodeType]);
    }
  }

  private filterTreeByActiveKeys(treeNodes: _AbcTreeNode[]): void {
    for (let node of treeNodes) {
      if (!this.abcExpressionsEditorService.nodesTypeFilter.value.includes(node.icon) && !node.children.length) {
        node.visible = false;
      } else {
        node.visible = true;
        this.filterTreeByActiveKeys(node.children);
      }
    }
  }

  private createTreeData(objectTypeInfo: ObjectTypeInfoViewModel): _AbcTreeNode[] {
    const rootNode = new _AbcTreeNode();
    rootNode.caption = this.neutralDesignationActive ? objectTypeInfo.name : objectTypeInfo.displayText;
    rootNode.data = objectTypeInfo;
    rootNode.id = objectTypeInfo.name;
    rootNode.icon = this.EMPTYSTRING;

    this.createTreeColumns(objectTypeInfo.columnsByName, rootNode);

    this.createTreeExpandableColumns(objectTypeInfo.referencesByName, rootNode);
    this.createTreeExpandableColumns(objectTypeInfo.referencingByName, rootNode);

    return [rootNode];
  }

  private async createTreeColumns(
    columns: Map<string, ObjectTypeInfoColumnViewModel>,
    parentNode: _AbcTreeNode
  ): Promise<void> {
    columns.forEach(async (columnInfo: ObjectTypeInfoColumnViewModel) => {
      await this.createColumn(columnInfo, parentNode);
      this.onColumnsDictionaryUpdated.emit();
    });
  }

  private async createColumn(columnInfo: ObjectTypeInfoColumnViewModel, parentNode: _AbcTreeNode): Promise<void> {
    let treeNode = new _AbcTreeNode();
    treeNode.caption = this.neutralDesignationActive ? columnInfo.name : columnInfo.displayText;
    treeNode.id = parentNode.id + '_' + columnInfo.name;
    treeNode.parentId = parentNode.id;
    treeNode.children = [];
    treeNode.data = columnInfo;

    if (
      columnInfo.columnType === ObjectTypeInfoColumnType.IdRef ||
      columnInfo.columnType === ObjectTypeInfoColumnType.PrimaryKey ||
      columnInfo.columnType === ObjectTypeInfoColumnType.ObjectType
    ) {
      treeNode.icon = ColumnTypeIcon.key;
    } else if (columnInfo.columnType === ObjectTypeInfoColumnType.Sys) {
      treeNode.icon = ColumnTypeIcon.preferences;
    } else {
      treeNode.icon = ColumnTypeIcon.box;
    }

    treeNode.parentNode = parentNode;
    parentNode.children.push(treeNode);
    if (!this.columnsDictionary.some((entry) => entry.key === columnInfo.displayText)) {
      this.columnsDictionary.push({ key: columnInfo.displayText, value: columnInfo.name });
    }
  }

  private createTreeExpandableColumns(
    data: Map<string, ObjectTypeInfoReferenceViewModel> | Map<string, ObjectTypeInfoReferencingViewModel>,
    parentNode: _AbcTreeNode
  ): void {
    data.forEach((element: ObjectTypeInfoReferenceViewModel | ObjectTypeInfoReferencingViewModel) => {
      const treeNode = new _AbcTreeNode();
      treeNode.caption = this.neutralDesignationActive ? element.name : element.displayText;
      treeNode.id = parentNode.id + '_' + element.name;
      treeNode.parentId = parentNode.id;
      treeNode.children = [];
      treeNode.data = element;
      treeNode.icon =
        element instanceof ObjectTypeInfoReferenceViewModel
          ? ColumnTypeIcon.reference
          : element instanceof ObjectTypeInfoReferencingViewModel
          ? ColumnTypeIcon.referencing
          : null;

      const subChildren = new _AbcTreeNode();
      subChildren.caption = this.EMPTYSTRING;
      subChildren.visible = false;

      // critical for tree search puposes !
      subChildren.caption = treeNode.caption;
      treeNode.children.push(subChildren);
      treeNode.parentNode = parentNode;
      parentNode.children.push(treeNode);

      if (!this.columnsDictionary.some((entry) => entry.key === element.displayText)) {
        this.columnsDictionary.push({ key: element.displayText, value: element.name });
        this.onColumnsDictionaryUpdated.emit();
      }
    });
  }

  private translateNode(node: _AbcTreeNode): void {
    if (node.data) {
      node.caption = this.neutralDesignationActive
        ? (node.data as ObjectTypeInfoReferenceViewModel).name
        : (node.data as ObjectTypeInfoReferenceViewModel).displayText;
    }

    if (node.children.length) {
      for (let child of node.children) {
        this.translateNode(child);
      }
    }
  }

  public refreshButtonsState(): void {
    this.entityButtons = [
      {
        id: EntityButtonId.pinnedBtn,
        icon: 'pin',
        toolTipContent: this.translation?.pin,

        statfullButton: true,
        active: this.pinned,
        action: async () => this.pin(),
      },
      {
        id: EntityButtonId.keyBtn,
        icon: 'key',
        toolTipContent: this.translation?.attribute.keyColumns,
        statfullButton: true,
        active: this.showKeyColumn,
        action: async () => this.toggleNodesByType(NodeTypeFilter.Key),
      },
      {
        id: EntityButtonId.preferenceBtn,
        icon: 'preferences',
        toolTipContent: this.translation?.attribute.systemColumns,
        statfullButton: true,
        active: this.showPrefernceColumn,
        action: async () => this.toggleNodesByType(NodeTypeFilter.system),
      },
    ];
  }
}
